import { useCallback, useEffect } from 'react';

import { FormattedMessage } from 'react-intl';

import classNames from 'classnames';
import { useLocation } from 'react-router-dom';
import type { List as ImmutableList } from 'immutable';

import { expandHomeTimeline } from 'mastodon/actions/timelines';
import { Avatar } from 'mastodon/components/avatar';
import { RelativeTimestamp } from 'mastodon/components/relative_timestamp';
import { useAppHistory } from 'mastodon/components/router';
import { ArticlePreviewCard } from 'mastodon/features/whocare_article/display/article_preview_card';
import type { ArticleCard } from 'mastodon/features/whocare_article/types';
import { useExpandedStatus } from 'mastodon/hooks/useStatus';
import { me } from 'mastodon/initial_state';
import { useAppDispatch, useAppSelector } from 'mastodon/store';

import { parseStatusPath } from './routes';

const MAX_EXCERPT = 110;

const excerpt = (text: string | undefined) => {
  const clean = (text ?? '').replace(/\s+/g, ' ').trim();
  const chars = Array.from(clean);

  return chars.length > MAX_EXCERPT
    ? `${chars.slice(0, MAX_EXCERPT).join('')}…`
    : clean;
};

const FollowRow: React.FC<{ id: string; activeId?: string }> = ({
  id,
  activeId,
}) => {
  const history = useAppHistory();
  const status = useExpandedStatus(id);
  const proper = status?.reblog ?? status;
  const properId = proper?.id;
  const acct = proper?.account.acct;

  const handleClick = useCallback(
    (event: React.MouseEvent) => {
      if (!properId || !acct) {
        return;
      }

      const path = `/@${acct}/${properId}`;

      if (event.metaKey || event.ctrlKey || event.shiftKey) {
        return; // let the browser open the link in a new tab / window
      }

      event.preventDefault();

      if (history.location.pathname !== path) {
        history.push(path, { focusTarget: false });
      }
    },
    [history, properId, acct],
  );

  // "Following" is about other people: leave out my own posts
  if (!status || !proper || status.account.id === me) {
    return null;
  }

  const name = proper.account.display_name || proper.account.username;
  const text = excerpt(proper.search_index);
  // whocare: an article row shows the compact card instead of the start of "title / excerpt / url"
  const article = (proper as unknown as { whocare_article?: ArticleCard | null })
    .whocare_article;
  const boostedBy =
    status.reblog && status.account.display_name
      ? status.account.display_name
      : status.account.username;

  return (
    <li>
      <a
        className={classNames('threepane-follow-row', {
          'threepane-follow-row--active': properId === activeId,
        })}
        href={`/@${acct}/${properId}`}
        onClick={handleClick}
      >
        <Avatar account={proper.account} size={32} />
        <span className='threepane-follow-row__body'>
          <span className='threepane-follow-row__head'>
            <bdi className='threepane-follow-row__name'>{name}</bdi>
            <span className='threepane-follow-row__time'>
              <RelativeTimestamp timestamp={proper.created_at} />
            </span>
          </span>
          {status.reblog && (
            <span className='threepane-follow-row__boost'>
              <FormattedMessage
                id='threepane.boosted_by'
                defaultMessage='Boosted by {name}'
                values={{ name: boostedBy }}
              />
            </span>
          )}
          {article ? (
            <ArticlePreviewCard
              article={article}
              compact
              hideCover={proper.sensitive}
              lang={proper.language}
            />
          ) : (
            <span className='threepane-follow-row__text'>
              {text || (
                <FormattedMessage
                  id='threepane.no_text'
                  defaultMessage='(attachment or empty post)'
                />
              )}
            </span>
          )}
        </span>
      </a>
    </li>
  );
};

export const FollowFeed: React.FC = () => {
  const dispatch = useAppDispatch();
  const items = useAppSelector(
    (state) =>
      state.timelines.getIn(['home', 'items']) as
        | ImmutableList<string | null>
        | undefined,
  );
  const isLoading = useAppSelector(
    (state) => !!state.timelines.getIn(['home', 'isLoading']),
  );
  const hasMore = useAppSelector(
    (state) => state.timelines.getIn(['home', 'hasMore'], true) as boolean,
  );
  const openId = parseStatusPath(useLocation().pathname)?.id;

  const ids = (items?.toArray() ?? [])
    .filter((id): id is string => typeof id === 'string' && /^\d+$/.test(id))
    .slice(0, 60);

  useEffect(() => {
    // The home timeline is normally loaded by the app shell; make sure it is when we mount first.
    if (!items || items.size === 0) {
      void dispatch(expandHomeTimeline());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const handleMore = useCallback(() => {
    const last = ids[ids.length - 1];
    void dispatch(expandHomeTimeline({ maxId: last }));
  }, [dispatch, ids]);

  return (
    <div className='threepane-follow-feed'>
      {ids.length === 0 && !isLoading && (
        <div className='threepane-follow-feed__empty'>
          <FormattedMessage
            id='threepane.follow_feed_empty'
            defaultMessage='Posts from people you follow will show up here.'
          />
        </div>
      )}
      <ul className='threepane-follow-feed__list'>
        {ids.map((id) => (
          <FollowRow key={id} id={id} activeId={openId} />
        ))}
      </ul>
      {hasMore && ids.length > 0 && (
        <button
          type='button'
          className='threepane-follow-feed__more'
          onClick={handleMore}
          disabled={isLoading}
        >
          <FormattedMessage
            id='threepane.load_more'
            defaultMessage='Load more'
          />
        </button>
      )}
    </div>
  );
};
